import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { Theme } from '@constants';
import { useGetAlertTileInventory, useMercatorMap } from '@hooks';

import { useBufferedCanvas } from './hooks/useBufferedCanvas';
import { AlertProductKey, AlertType, MapAlertTile } from './MapAlertTile';
import { MapSVGLayer, Path } from './MapSVGLayer';

import { isWeb } from '../../helpers/Platform';

export { AlertType, AlertProductKey };

const MapAlertTileLayerPhase = {
  // Getting the map ready, setting the size, etc.
  initializing: 0,
  // Create the tile components, fetch and parse data.
  preparingTiles: 1,
  // Draw the alert polygons.
  drawingToCanvas: 2,
  // Show the image.  Will reset to preparingTiles when input changes.
  finished: 3,
};

const minSeverityCode = 1;
const maxSeverityCode = 5;

const polygonsToDraw = tilesLoaded => {
  const drawnPolygonIdentifiers = [];
  const polygons = [];

  for (const drawStroke of [false, true]) {
    for (let severityCode = maxSeverityCode; severityCode >= minSeverityCode; severityCode--) {
      polygons.push(
        ...Object.values(tilesLoaded).flatMap(timePolygons =>
          Object.values(timePolygons).flatMap(tilePolygons =>
            tilePolygons.map(tilePolygon => {
              if (tilePolygon.severityCode !== severityCode) {
                return null;
              }

              if ((drawStroke && tilePolygon.strokeColor == null) || (!drawStroke && tilePolygon.fillColor == null)) {
                return null;
              }

              const alreadyDrawn = drawnPolygonIdentifiers.includes(tilePolygon.id);

              if (alreadyDrawn) {
                return null;
              }
              drawnPolygonIdentifiers.push(tilePolygon.id);

              return tilePolygon;
            }),
          ),
        ),
      );
    }
  }

  return polygons.filter(Boolean);
};

// Draw the polygons
const draw = (tilesLoaded, canvas) => {
  if (canvas == null) {
    return;
  }

  const context = canvas.getContext('2d');

  context.clearRect(0, 0, canvas.width, canvas.height);

  for (const tilePolygon of polygonsToDraw(tilesLoaded)) {
    const path = new Path2D(tilePolygon.svgPath);

    context.strokeStyle = tilePolygon.strokeColor;
    context.lineWidth = 3;
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.fillStyle = tilePolygon.fillColor;

    if (tilePolygon.strokeColor != null) {
      context.stroke(path);
    }
    if (tilePolygon.fillColor != null) {
      context.fill(path);
    }
  }
};

const alertPaths = tilesLoaded => {
  const pathComponents = [];
  let keyIndex = 0;

  for (const tilePolygon of polygonsToDraw(tilesLoaded)) {
    pathComponents.push(
      <Path
        key={`path-${keyIndex++}`}
        d={tilePolygon.svgPath}
        fill={tilePolygon.fillColor}
        lineCap={'round'}
        lineJoin={'round'}
        stroke={tilePolygon.strokeColor}
        strokeWidth={2}
      />,
    );
  }

  return pathComponents;
};

const useInventoryTimeForProductKey = productKey => {
  const { data: inventoryData } = useGetAlertTileInventory(productKey);

  if (
    inventoryData?.data?.products != null &&
    inventoryData.data.products[productKey] != null &&
    inventoryData.data.products[productKey].time != null &&
    inventoryData.data.products[productKey].time.length > 0
  ) {
    return inventoryData.data.products[productKey].time[0];
  }

  return null;
};

const MapAlertTileLayerCompleteAPI = forwardRef(
  ({ center, zoom, tileDimension, onFinishedDrawing, alpha, showAlertTypes }, reference) => {
    const productKeys = {};

    const marineTime = useInventoryTimeForProductKey(AlertProductKey.marine);

    if (marineTime != null) {
      productKeys[AlertProductKey.marine] = marineTime;
    }

    const severeTime = useInventoryTimeForProductKey(AlertProductKey.severe);

    if (severeTime != null) {
      productKeys[AlertProductKey.severe] = severeTime;
    }

    const floodTime = useInventoryTimeForProductKey(AlertProductKey.flood);

    if (floodTime != null) {
      productKeys[AlertProductKey.flood] = floodTime;
    }

    const winterTime = useInventoryTimeForProductKey(AlertProductKey.winter);

    if (winterTime != null) {
      productKeys[AlertProductKey.winter] = winterTime;
    }

    const otherTime = useInventoryTimeForProductKey(AlertProductKey.other);

    if (otherTime != null) {
      productKeys[AlertProductKey.other] = otherTime;
    }

    const tropicalTime = useInventoryTimeForProductKey(AlertProductKey.tropical);

    if (tropicalTime != null) {
      productKeys[AlertProductKey.tropical] = tropicalTime;
    }

    return Object.keys(productKeys).length === 6 ? (
      <MapAlertTileLayerUI
        alpha={alpha}
        center={center}
        productKeys={productKeys}
        ref={reference}
        showAlertTypes={showAlertTypes}
        tileDimension={tileDimension}
        zoom={zoom}
        onFinishedDrawing={onFinishedDrawing}
      />
    ) : null;
  },
);

const MapAlertTileLayerSevereAPI = forwardRef(
  (
    { center, zoom, tileDimension = 512, onFinishedDrawing, alpha = 1, showAlertTypes = Object.values(AlertType) },
    reference,
  ) => {
    const productKeys = {};

    const severeTime = useInventoryTimeForProductKey(AlertProductKey.severe);

    if (severeTime != null) {
      productKeys[AlertProductKey.severe] = severeTime;
    }

    const floodTime = useInventoryTimeForProductKey(AlertProductKey.flood);

    if (floodTime != null) {
      productKeys[AlertProductKey.flood] = floodTime;
    }

    return Object.keys(productKeys).length === 2 ? (
      <MapAlertTileLayerUI
        alpha={alpha}
        center={center}
        productKeys={productKeys}
        ref={reference}
        showAlertTypes={showAlertTypes}
        tileDimension={tileDimension}
        zoom={zoom}
        onFinishedDrawing={onFinishedDrawing}
      />
    ) : null;
  },
);

const MapAlertTileLayerUI = forwardRef(
  (
    {
      center,
      zoom,
      // Object with AlertProductKey keys and a time epoch number/string that should be used to fetch the product.
      productKeys,
      tileDimension = 512,
      onFinishedDrawing,
      alpha = 1,
      showAlertTypes = Object.values(AlertType),
    },
    reference,
  ) => {
    /** Phase Management **/

    const [phase, setPhase] = useState(MapAlertTileLayerPhase.initializing);

    const componentLayoutComplete = () => {
      if (phase !== MapAlertTileLayerPhase.initializing) {
        console.log(`Warning, layout complete called when phase wasn't initializing. (${phase})`);

        // onLayout could be called when adding/removing layers.
        // We only want to capture the first layout complete, so return here.
        return;
      }

      setPhase(MapAlertTileLayerPhase.preparingTiles);
    };

    const finishedPreparingTiles = () => {
      if (phase !== MapAlertTileLayerPhase.preparingTiles) {
        console.log(`Warning, finished preparing tiles called when we weren't preparing tiles. (${phase})`);
      }

      bufferedCanvas.setNeedsDisplay();
      setPhase(MapAlertTileLayerPhase.drawingToCanvas);
    };

    const finishedDrawing = () => {
      if (phase !== MapAlertTileLayerPhase.drawingToCanvas) {
        console.log(`Warning, finished drawing called when we weren't drawing. (${phase})`);
      }

      setPhase(MapAlertTileLayerPhase.finished);

      if (onFinishedDrawing !== undefined) {
        onFinishedDrawing();
      }
    };

    const productKeysJsonString = JSON.stringify(productKeys);
    const previousEnvironment = useRef(null);

    useEffect(() => {
      const environment = `${center}-${zoom}-${tileDimension}-${productKeysJsonString}`;

      if (
        environment !== previousEnvironment.current &&
        phase !== MapAlertTileLayerPhase.initializing &&
        phase !== MapAlertTileLayerPhase.preparingTiles
      ) {
        setTilesLoaded({});
        setPhase(MapAlertTileLayerPhase.preparingTiles);
        previousEnvironment.current = environment;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [center, zoom, productKeysJsonString, tileDimension]);

    // Used on Native devices to cache the SVG paths.
    const [svgPathComponents, setSVGPathComponents] = useState([]);

    // If the phase changes to drawingToCanvas and we are on native, automatically push to finished drawing because we are using SVG instead of a buffered canvas.
    useEffect(() => {
      if (!isWeb && phase === MapAlertTileLayerPhase.drawingToCanvas) {
        setSVGPathComponents(alertPaths(tilesLoaded));
        finishedDrawing();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    /** State **/

    // This is the size of the rendered map component.  We use this while calculating map zooms.
    const [mapWidth, setMapWidth] = useState(null);
    const [mapHeight, setMapHeight] = useState(null);

    const bufferedCanvas = useBufferedCanvas({
      width: mapWidth,
      height: mapHeight,
      draw: canvas => {
        if (phase === MapAlertTileLayerPhase.drawingToCanvas) {
          // Draw if we are in the drawing phase.
          draw(tilesLoaded, canvas);
          finishedDrawing();
          return true;
        } else if (phase === MapAlertTileLayerPhase.finished) {
          // Draw if we have finished drawing once, but the canvas may have changed.
          draw(tilesLoaded, canvas);
          return true;
        } else {
          // We aren't ready to draw yet, return false so the
          // buffered canvas isn't replaced by an empty one.
          return false;
        }
      },
    });

    const mercatorMap = useMercatorMap({
      center,
      zoom,
      tileDimension,
      viewWidth: (mapWidth ?? 1) * Theme.pixelRatio(),
      viewHeight: (mapHeight ?? 1) * Theme.pixelRatio(),
    });

    /** Tracking Tiles Loaded **/

    const [tilesLoaded, setTilesLoaded] = useState({});
    const tilesNeeded = useRef([]);
    const startLoadingTile = (time, tileKey) => {
      if (!tilesNeeded.current.includes([time, tileKey])) {
        tilesNeeded.current.push([time, tileKey]);
      }
    };

    const tilesStillLoading = () => {
      let numberOfTilesLoading = 0;

      for (const [time, tileKey] of tilesNeeded.current) {
        if (tilesLoaded[time] === undefined || tilesLoaded[time][tileKey] === undefined) {
          numberOfTilesLoading++;
        }
      }

      return numberOfTilesLoading;
    };

    // If a time doesn't exist in tilesLoaded, create an empty object for it.
    const checkLoadingTimeExists = time => {
      if (tilesLoaded[time] === undefined) {
        const newTilesLoaded = tilesLoaded;

        newTilesLoaded[time] = {};
        setTilesLoaded(newTilesLoaded);
      }
    };

    // Remove any data for a specified tile at any time.  This is called before recording new tile data.
    const removeTileKey = tileKey => {
      const newTilesLoaded = tilesLoaded;

      for (const timeTiles of Object.values(newTilesLoaded)) {
        delete timeTiles[tileKey];
      }

      setTilesLoaded(newTilesLoaded);
    };

    const finishLoadingTile = (time, tileKey, result) => {
      checkLoadingTimeExists(time);

      if (tilesLoaded[time][tileKey] === result) {
        return;
      }

      // Remove any old data for this tile.
      removeTileKey(tileKey);

      // Store the new value.
      const newTilesLoaded = tilesLoaded;

      newTilesLoaded[time][tileKey] = result;
      setTilesLoaded(newTilesLoaded);

      if (tilesStillLoading() === 0) {
        finishedPreparingTiles();
      }
    };

    /** Preparing Tiles **/

    let tiles = null;

    if (phase === MapAlertTileLayerPhase.preparingTiles) {
      tilesNeeded.current = [];

      const upperLeftCoords = mercatorMap.viewPointToCoords(0, 0);
      const bottomRightCoords = mercatorMap.viewPointToCoords(
        mapWidth * Theme.pixelRatio(),
        mapHeight * Theme.pixelRatio(),
      );

      const minRow = mercatorMap.tileRowForLatitude(upperLeftCoords[1]);
      const maxRow = mercatorMap.tileRowForLatitude(bottomRightCoords[1]);
      const minCol = mercatorMap.tileColumnForLongitude(upperLeftCoords[0]);
      const maxCol = mercatorMap.tileColumnForLongitude(bottomRightCoords[0]);

      const tileZoom = Math.floor(mercatorMap.tileZoom);

      const tileComponents = [];

      for (const productKey of Object.keys(productKeys ?? [])) {
        const time = productKeys[productKey];

        for (let row = minRow; row <= maxRow; row++) {
          for (let col = minCol; col <= maxCol; col++) {
            const tileKey = `${productKey} ${col} ${row} ${tileZoom}`;

            startLoadingTile(time, tileKey);

            tileComponents.push(
              <MapAlertTile
                key={tileKey}
                mercatorMap={mercatorMap}
                productKey={productKey}
                showAlertTypes={showAlertTypes}
                time={time}
                x={col}
                y={row}
                z={tileZoom}
                onTileLoaded={paths => {
                  finishLoadingTile(time, tileKey, paths);
                }}
              />,
            );
          }
        }
      }

      tiles = tileComponents;
    }

    if (phase === MapAlertTileLayerPhase.finished && onFinishedDrawing != null) {
      onFinishedDrawing();
    }
    return (
      <View
        ref={reference}
        style={[{ opacity: alpha }, styles.layerParent]}
        onLayout={event => {
          const { width, height } = event.nativeEvent.layout;

          if (width != null && width !== mapWidth) {
            setMapWidth(width);
          }
          if (height != null && height !== mapHeight) {
            setMapHeight(height);
          }

          componentLayoutComplete();
        }}
      >
        {phase !== MapAlertTileLayerPhase.initializing &&
          (isWeb ? (
            bufferedCanvas.components()
          ) : (
            <MapSVGLayer height={mapHeight} width={mapWidth}>
              {svgPathComponents}
            </MapSVGLayer>
          ))}
        {tiles}
      </View>
    );
  },
);

export const MapAlertTileLayer = {
  CompleteAPI: MapAlertTileLayerCompleteAPI,
  SevereAPI: MapAlertTileLayerSevereAPI,
  UI: MapAlertTileLayerUI,
};

MapAlertTileLayerCompleteAPI.displayName = 'MapAlertTileLayerCompleteAPI';

// MapAlertTileLayerCompleteAPI.propTypes = {
//   center: PropTypes.arrayOf(PropTypes.number).isRequired,
//   zoom: PropTypes.number.isRequired,
//   // Called when the layer is fully rendered on the map.
//   onFinishedDrawing: PropTypes.func,
//   // We aren't tiling images, but this is used to convert between
//   // lat/lon and view coordinates.  This should match the dimension
//   // of the weather tiles fetched from api.weather.com.
//   tileDimension: PropTypes.number,
//   alpha: PropTypes.number,
//   showAlertTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AlertType))),
// };

MapAlertTileLayerCompleteAPI.defaultProps = {
  onFinishedDrawing: () => {},
  tileDimension: 512,
  alpha: 1,
  showAlertTypes: Object.values(AlertType),
};

MapAlertTileLayerSevereAPI.displayName = 'MapAlertTileLayerSevereAPI';

// MapAlertTileLayerSevereAPI.propTypes = {
//   center: PropTypes.arrayOf(PropTypes.number).isRequired,
//   zoom: PropTypes.number.isRequired,
//   // Called when the layer is fully rendered on the map.
//   onFinishedDrawing: PropTypes.func,
//   // We aren't tiling images, but this is used to convert between
//   // lat/lon and view coordinates.  This should match the dimension
//   // of the weather tiles fetched from api.weather.com.
//   tileDimension: PropTypes.number,
//   alpha: PropTypes.number,
//   showAlertTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AlertType))),
// };

MapAlertTileLayerSevereAPI.defaultProps = {
  onFinishedDrawing: () => {},
  tileDimension: 512,
  alpha: 1,
  showAlertTypes: Object.values(AlertType),
};

const styles = StyleSheet.create({
  hidden: {
    opacity: 0,
  },
  layerParent: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    overflow: 'hidden',
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
});
